import { useState, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Plus,
  RefreshCw,
  Loader2,
  Trash2,
  TrendingUp,
  Target,
  ArrowUp,
  ArrowDown,
  Minus,
  Search,
  List,
  Sparkles,
  CheckCircle2,
  BarChart3,
  ChevronDown,
  Settings2,
  Star,
  MapPin,
  HelpCircle,
  Play,
} from 'lucide-react'
import {
  useKeywords,
  useAddKeywords,
  useRemoveKeyword,
  useCheckKeywords,
  useKeywordHistory,
  useEnrichKeywords,
  type KeywordTarget,
} from '../hooks/useKeywords'
import { useGscQueries } from '../hooks/useGsc'
import { useAiOverview } from '@/client/modules/site/hooks/useAiOverview'
import { rankColor } from '@/client/lib/colors'

interface RankTrackerTabProps {
  siteId: string
  domain: string
  siteUrl?: string // GSC site URL for suggestions
  defaultLocation?: string
  defaultEngine?: string
}

// ── Sorting ──────────────────────────────────────────────────────

type SortKey = 'keyword' | 'rank' | 'change' | 'volume' | 'cpc' | 'competition' | 'difficulty'
type SortDir = 'asc' | 'desc'

function sortKeywords(keywords: KeywordTarget[], key: SortKey, dir: SortDir): KeywordTarget[] {
  return [...keywords].sort((a, b) => {
    let cmp = 0
    switch (key) {
      case 'keyword':
        cmp = a.keyword.localeCompare(b.keyword)
        break
      case 'rank':
        // Unranked goes to bottom regardless of direction
        if (a.latestRank == null && b.latestRank == null) cmp = 0
        else if (a.latestRank == null) cmp = 1
        else if (b.latestRank == null) cmp = -1
        else cmp = a.latestRank - b.latestRank
        break
      case 'change': {
        const deltaA = getDelta(a)
        const deltaB = getDelta(b)
        if (deltaA == null && deltaB == null) cmp = 0
        else if (deltaA == null) cmp = 1
        else if (deltaB == null) cmp = -1
        else cmp = deltaA - deltaB
        break
      }
      case 'volume':
        cmp = (a.searchVolume ?? 0) - (b.searchVolume ?? 0)
        break
      case 'cpc':
        cmp = (a.cpc ?? 0) - (b.cpc ?? 0)
        break
      case 'competition':
        cmp = (a.competition ?? 0) - (b.competition ?? 0)
        break
      case 'difficulty':
        cmp = (a.difficulty ?? -1) - (b.difficulty ?? -1)
        break
    }
    return dir === 'desc' ? -cmp : cmp
  })
}

function getDelta(kw: KeywordTarget): number | null {
  if (kw.latestRank == null || kw.previousRank == null) return null
  return kw.previousRank - kw.latestRank // positive = improved
}

// ── Components ───────────────────────────────────────────────────

function RankBadge({ rank, bestRank }: { rank: number | null; bestRank: number | null }) {
  if (rank == null) {
    return <span className="text-muted-foreground text-sm">-</span>
  }

  const color = rankColor(rank)

  return (
    <div className="flex items-center gap-2">
      <span className={`font-bold tabular-nums ${color}`}>{rank}</span>
      {bestRank != null && bestRank < rank && (
        <span className="text-xs text-muted-foreground" title={`Best: ${bestRank}`}>
          (best: {bestRank})
        </span>
      )}
    </div>
  )
}

function RankChange({ current, previous }: { current: number | null; previous: number | null }) {
  if (current == null && previous == null) {
    return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">New</Badge>
  }
  if (current == null || previous == null) {
    return <Minus className="h-3.5 w-3.5 text-muted-foreground" />
  }

  const delta = previous - current // positive = improved (lower rank number)

  if (delta === 0) {
    return <Minus className="h-3.5 w-3.5 text-muted-foreground" />
  }

  if (delta > 0) {
    return (
      <span className="flex items-center gap-0.5 text-green-600 dark:text-green-400 text-sm font-medium tabular-nums">
        <ArrowUp className="h-3 w-3" />{delta}
      </span>
    )
  }

  return (
    <span className="flex items-center gap-0.5 text-red-600 dark:text-red-400 text-sm font-medium tabular-nums">
      <ArrowDown className="h-3 w-3" />{Math.abs(delta)}
    </span>
  )
}

function DistributionSummary({ keywords }: { keywords: KeywordTarget[] }) {
  const dist = useMemo(() => {
    let top3 = 0, top10 = 0, top20 = 0, over20 = 0, unranked = 0
    for (const kw of keywords) {
      if (kw.latestRank == null) { unranked++; continue }
      if (kw.latestRank <= 3) top3++
      else if (kw.latestRank <= 10) top10++
      else if (kw.latestRank <= 20) top20++
      else over20++
    }
    return { top3, top10, top20, over20, unranked }
  }, [keywords])

  if (keywords.length === 0) return null

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <DistBadge label="Top 3" count={dist.top3} color="bg-green-500/15 text-green-700 dark:text-green-400" />
      <DistBadge label="Top 10" count={dist.top10} color="bg-blue-500/15 text-blue-700 dark:text-blue-400" />
      <DistBadge label="Top 20" count={dist.top20} color="bg-yellow-500/15 text-yellow-700 dark:text-yellow-400" />
      <DistBadge label="20+" count={dist.over20} color="bg-muted text-muted-foreground" />
      {dist.unranked > 0 && (
        <DistBadge label="Not ranked" count={dist.unranked} color="bg-muted text-muted-foreground" />
      )}
    </div>
  )
}

function DistBadge({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${color}`}>
      {label}: <span className="font-bold tabular-nums">{count}</span>
    </span>
  )
}

function MiniSparkline({ siteId, keywordId }: { siteId: string; keywordId: string }) {
  const { data, isLoading } = useKeywordHistory(siteId, keywordId, 30)
  if (isLoading || !data?.history?.length) return null

  const history = data.history
  const positions = history.map((h) => h.position).filter((p): p is number => p != null)
  if (positions.length < 2) return null

  const max = Math.max(...positions) + 5
  const min = Math.min(...positions) - 2
  const range = max - min || 1

  return (
    <div className="flex items-end gap-px h-5 w-16" title="30-day rank history (lower = better)">
      {positions.slice(-15).map((pos, i) => {
        const height = ((max - pos) / range) * 100
        const color = pos <= 3 ? 'bg-green-500' : pos <= 10 ? 'bg-blue-500' : pos <= 20 ? 'bg-yellow-500' : 'bg-muted-foreground/40'
        return (
          <div
            key={i}
            className={`flex-1 min-w-[2px] rounded-t-sm ${color}`}
            style={{ height: `${Math.max(height, 8)}%` }}
            title={`#${pos}`}
          />
        )
      })}
    </div>
  )
}

// ── Add Keywords Dialog ──────────────────────────────────────────

function AddKeywordsDialog({ siteId, domain, existingKeywords, defaultLocation, defaultEngine }: {
  siteId: string
  domain: string
  existingKeywords: KeywordTarget[]
  defaultLocation?: string
  defaultEngine?: string
}) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<'suggested' | 'manual'>('suggested')
  const [text, setText] = useState('')
  const [selectedQueries, setSelectedQueries] = useState<Set<string>>(new Set())
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [location, setLocation] = useState('')
  const [searchEngine, setSearchEngine] = useState<string>('')
  const addKeywords = useAddKeywords(siteId)

  // Fetch GSC suggestions (90 days, top 50)
  const { data: gscData, isLoading: gscLoading } = useGscQueries(siteId, '', 90, 50)

  const existingSet = useMemo(
    () => new Set(existingKeywords.map((k) => k.keyword.toLowerCase())),
    [existingKeywords]
  )

  const suggestions = useMemo(() => {
    if (!gscData?.queries) return []
    return gscData.queries
      .filter((q) => q.query && q.impressions > 0)
      .sort((a, b) => b.impressions - a.impressions)
  }, [gscData])

  const gscConnected = !gscLoading && gscData?.queries != null

  const getAdvancedParams = () => {
    const params: { location?: string; searchEngine?: string } = {}
    if (location.trim()) params.location = location.trim()
    if (searchEngine && searchEngine !== '__default__') params.searchEngine = searchEngine
    return params
  }

  const handleAddManual = () => {
    const keywords = text.split('\n').map((l) => l.trim()).filter(Boolean)
    if (keywords.length === 0) return

    addKeywords.mutate({ keywords, ...getAdvancedParams() }, {
      onSuccess: () => {
        setText('')
        setOpen(false)
      },
    })
  }

  const handleAddSuggested = () => {
    if (selectedQueries.size === 0) return
    const keywords = Array.from(selectedQueries)
    addKeywords.mutate({ keywords, ...getAdvancedParams() }, {
      onSuccess: () => {
        setSelectedQueries(new Set())
        setOpen(false)
      },
    })
  }

  const toggleQuery = (query: string) => {
    setSelectedQueries((prev) => {
      const next = new Set(prev)
      if (next.has(query)) next.delete(query)
      else next.add(query)
      return next
    })
  }

  const selectAll = () => {
    const available = suggestions.filter((s) => !existingSet.has(s.query.toLowerCase()))
    setSelectedQueries(new Set(available.map((s) => s.query)))
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setSelectedQueries(new Set()); setText(''); setLocation(''); setSearchEngine(''); setShowAdvanced(false) } }}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Keywords
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Keywords to Track</DialogTitle>
          <DialogDescription>
            Track keyword rankings for {domain}. All keywords use mobile device.
          </DialogDescription>
        </DialogHeader>

        {/* Tab switcher */}
        <div className="flex items-center gap-1 border-b">
          <button
            onClick={() => setTab('suggested')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'suggested'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Suggested
          </button>
          <button
            onClick={() => setTab('manual')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'manual'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <List className="h-3.5 w-3.5" />
            Manual
          </button>
        </div>

        {tab === 'suggested' && (
          <div className="flex-1 overflow-hidden flex flex-col gap-3">
            {!gscConnected ? (
              <div className="text-center py-6">
                <Search className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-2">
                  Connect Search Console for keyword suggestions.
                </p>
                <Button variant="outline" size="sm" asChild>
                  <a href="/dashboard/integrations">Manage Connections</a>
                </Button>
                <button
                  onClick={() => setTab('manual')}
                  className="block mx-auto mt-3 text-xs text-muted-foreground hover:text-foreground"
                >
                  Or add keywords manually
                </button>
              </div>
            ) : gscLoading ? (
              <div className="space-y-2 py-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : suggestions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No search queries found. Try adding keywords manually.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {selectedQueries.size} selected
                  </span>
                  <button
                    onClick={selectAll}
                    className="text-xs text-primary hover:underline"
                  >
                    Select all available
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-0.5 max-h-[40vh]">
                  {suggestions.map((s) => {
                    const tracked = existingSet.has(s.query.toLowerCase())
                    return (
                      <label
                        key={s.query}
                        className={`flex items-center gap-3 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-accent/50 ${
                          tracked ? 'opacity-50' : ''
                        }`}
                      >
                        <Checkbox
                          checked={tracked || selectedQueries.has(s.query)}
                          disabled={tracked}
                          onCheckedChange={() => toggleQuery(s.query)}
                        />
                        <span className="flex-1 min-w-0 truncate">{s.query}</span>
                        <span className="text-xs text-muted-foreground tabular-nums shrink-0 w-10 text-right" title="Avg position">
                          #{s.position.toFixed(0)}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums shrink-0 w-12 text-right" title="Clicks">
                          {s.clicks.toLocaleString()}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums shrink-0 w-14 text-right" title="Impressions">
                          {s.impressions.toLocaleString()}
                        </span>
                      </label>
                    )
                  })}
                </div>
                <Button
                  onClick={handleAddSuggested}
                  disabled={addKeywords.isPending || selectedQueries.size === 0}
                  className="w-full"
                >
                  {addKeywords.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4" />
                  )}
                  Add {selectedQueries.size} Keyword{selectedQueries.size !== 1 ? 's' : ''}
                </Button>
              </>
            )}
          </div>
        )}

        {tab === 'manual' && (
          <div className="space-y-3">
            <Textarea
              placeholder="Enter keywords to track, one per line"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
            />
            <p className="text-xs text-muted-foreground">
              {text.split('\n').filter((l) => l.trim()).length} keywords
            </p>
            {addKeywords.isError && (
              <p className="text-sm text-destructive">Failed to add keywords.</p>
            )}
            <Button
              onClick={handleAddManual}
              disabled={addKeywords.isPending || !text.trim()}
              className="w-full"
            >
              {addKeywords.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Add Keywords
            </Button>
          </div>
        )}

        {/* Advanced options (shared across both tabs) */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <Settings2 className="h-3 w-3" />
          Advanced
          <ChevronDown className={`h-3 w-3 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
        </button>
        {showAdvanced && (
          <div className="grid grid-cols-2 gap-3 border rounded-lg p-3 bg-muted/30">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Location</label>
              <Input
                placeholder={defaultLocation ?? 'Australia'}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Search Engine</label>
              <Select value={searchEngine || '__default__'} onValueChange={setSearchEngine}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder={`Default (${defaultEngine ?? 'Google'})`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">Default ({defaultEngine === 'bing' ? 'Bing' : 'Google'})</SelectItem>
                  <SelectItem value="google">Google</SelectItem>
                  <SelectItem value="bing">Bing</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ── Check Now Button ─────────────────────────────────────────────

function CheckNowButton({ siteId }: { siteId: string }) {
  const { startCheck, progress } = useCheckKeywords(siteId)

  const isActive = progress.state !== 'idle'
  const showSuccess = progress.state === 'done' && progress.collected > 0

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant={showSuccess ? 'default' : 'outline'}
        onClick={startCheck}
        disabled={isActive}
        className={showSuccess ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
      >
        {progress.state === 'posted' && (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        )}
        {progress.state === 'collecting' && (
          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
        )}
        {progress.state === 'done' && progress.collected > 0 && (
          <CheckCircle2 className="mr-2 h-4 w-4" />
        )}
        {progress.state === 'idle' && (
          <RefreshCw className="mr-2 h-4 w-4" />
        )}
        {progress.state === 'idle' ? 'Check Now' :
         progress.state === 'posted' ? 'Checking...' :
         progress.state === 'collecting' ? 'Collecting...' :
         progress.collected > 0 ? 'Updated' : 'Check Now'}
      </Button>
      {progress.message && (
        <span className="text-xs text-muted-foreground">{progress.message}</span>
      )}
    </div>
  )
}

// ── SERP Features ────────────────────────────────────────────────

const SERP_FEATURE_ICONS: Record<string, { icon: typeof Star; label: string }> = {
  featured_snippet: { icon: Star, label: 'Featured Snippet' },
  local_pack: { icon: MapPin, label: 'Local Pack' },
  people_also_ask: { icon: HelpCircle, label: 'People Also Ask' },
  video: { icon: Play, label: 'Video' },
  ai_overview: { icon: Sparkles, label: 'AI Overview' },
  knowledge_graph: { icon: Search, label: 'Knowledge Graph' },
}

function SerpFeatureIcons({ features, aiOverviewPresent }: { features: string[] | null; aiOverviewPresent?: boolean }) {
  if (!features?.length) return <span className="text-xs text-muted-foreground">-</span>
  return (
    <div className="flex items-center gap-0.5 justify-center">
      {features.slice(0, 4).map((f) => {
        const config = SERP_FEATURE_ICONS[f]
        if (!config) return null
        const Icon = config.icon
        // AI Overview: amber/gold if domain is cited, muted if not
        const isAiOverview = f === 'ai_overview'
        const colorClass = isAiOverview && aiOverviewPresent
          ? 'text-amber-500 dark:text-amber-400'
          : 'text-muted-foreground hover:text-foreground'
        const label = isAiOverview && aiOverviewPresent
          ? `${config.label} (Your site cited)`
          : config.label
        return (
          <span key={f} title={label} className={`${colorClass} transition-colors`}>
            <Icon className="h-3 w-3" />
          </span>
        )
      })}
    </div>
  )
}

function IntentBadge({ intent }: { intent: string | null }) {
  if (!intent) return <span className="text-xs text-muted-foreground">-</span>
  const config: Record<string, { label: string; color: string }> = {
    informational: { label: 'Info', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    navigational: { label: 'Nav', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
    commercial: { label: 'Comm', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
    transactional: { label: 'Trans', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  }
  const c = config[intent] ?? { label: intent.slice(0, 4), color: 'bg-muted text-muted-foreground' }
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${c.color}`} title={intent}>
      {c.label}
    </span>
  )
}

function KdBadge({ difficulty }: { difficulty: number | null }) {
  if (difficulty == null) return <span className="text-xs text-muted-foreground">-</span>
  const color = difficulty <= 30 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
    : difficulty <= 60 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium tabular-nums ${color}`}>
      {difficulty}
    </span>
  )
}

// ── Keyword Row ──────────────────────────────────────────────────

function KeywordRow({ keyword, siteId }: { keyword: KeywordTarget; siteId: string }) {
  const removeKeyword = useRemoveKeyword(siteId)

  return (
    <tr className="border-b border-border/50 last:border-0 group">
      <td className="py-2">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{keyword.keyword}</span>
          {keyword.searchEngine !== 'google' && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 font-normal">bing</Badge>
          )}
          {keyword.location !== 'Australia' && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 font-normal">{keyword.location}</Badge>
          )}
        </div>
      </td>
      <td className="py-2 text-right">
        <RankBadge rank={keyword.latestRank} bestRank={keyword.bestRank} />
      </td>
      <td className="py-2 text-center">
        <RankChange current={keyword.latestRank} previous={keyword.previousRank} />
      </td>
      <td className="py-2 text-center">
        <MiniSparkline siteId={siteId} keywordId={keyword.id} />
      </td>
      <td className="py-2 text-right tabular-nums text-sm text-muted-foreground">
        {keyword.searchVolume?.toLocaleString() ?? '-'}
      </td>
      <td className="py-2 text-right tabular-nums text-sm text-muted-foreground">
        {keyword.cpc != null ? `$${keyword.cpc.toFixed(2)}` : '-'}
      </td>
      <td className="py-2 text-right tabular-nums text-sm text-muted-foreground">
        {keyword.competition != null ? `${(keyword.competition * 100).toFixed(0)}%` : '-'}
      </td>
      <td className="py-2 text-center">
        <KdBadge difficulty={keyword.difficulty} />
      </td>
      <td className="py-2 text-center">
        <IntentBadge intent={keyword.intent} />
      </td>
      <td className="py-2 text-center hidden xl:table-cell">
        <SerpFeatureIcons features={keyword.serpFeatures} aiOverviewPresent={keyword.aiOverviewPresent} />
      </td>
      <td className="py-2 text-right">
        {keyword.latestUrl && (
          <span className="text-xs text-muted-foreground truncate max-w-[150px] inline-block" title={keyword.latestUrl}>
            {(() => { try { return new URL(keyword.latestUrl).pathname } catch { return keyword.latestUrl } })()}
          </span>
        )}
      </td>
      <td className="py-2 text-right">
        <Button
          variant="ghost"
          size="sm"
          className="opacity-40 group-hover:opacity-100 focus:opacity-100 text-destructive hover:text-destructive h-6 w-6 p-0"
          aria-label={`Remove ${keyword.keyword}`}
          onClick={() => removeKeyword.mutate(keyword.id)}
          disabled={removeKeyword.isPending}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </td>
    </tr>
  )
}

// ── Sortable Header ──────────────────────────────────────────────

function SortHeader({ label, sortKey, currentKey, currentDir, onSort, className }: {
  label: string
  sortKey: SortKey
  currentKey: SortKey
  currentDir: SortDir
  onSort: (key: SortKey) => void
  className?: string
}) {
  const active = currentKey === sortKey
  return (
    <th
      className={`pb-2 font-medium cursor-pointer select-none hover:text-foreground transition-colors ${className ?? ''} ${active ? 'text-foreground' : ''}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (
          currentDir === 'asc'
            ? <ArrowUp className="h-3 w-3" />
            : <ArrowDown className="h-3 w-3" />
        )}
      </span>
    </th>
  )
}

// ── Last Checked ─────────────────────────────────────────────────

function LastChecked({ keywords }: { keywords: KeywordTarget[] }) {
  const latest = useMemo(() => {
    let max: string | null = null
    for (const kw of keywords) {
      if (kw.latestCheckedAt && (!max || kw.latestCheckedAt > max)) {
        max = kw.latestCheckedAt
      }
    }
    return max
  }, [keywords])

  if (!latest) return <span className="text-xs text-muted-foreground">Never checked</span>

  const ago = getTimeAgo(latest)
  return <span className="text-xs text-muted-foreground">Last checked: {ago}</span>
}

function getTimeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

// ── AI Overview Section ──────────────────────────────────────────

function AiOverviewSection({ siteId, keywords }: { siteId: string; keywords: KeywordTarget[] }) {
  const { data } = useAiOverview(siteId)

  // Count from keyword data directly for quick display
  const kwWithAio = keywords.filter((k) => k.aiOverviewExists).length
  const kwCited = keywords.filter((k) => k.aiOverviewPresent).length

  if (kwWithAio === 0 && !data) return null

  const total = keywords.length
  const aioPercent = total > 0 ? Math.round((kwWithAio / total) * 100) : 0
  const citedPercent = total > 0 ? Math.round((kwCited / total) * 100) : 0

  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-purple-500" />
          <span className="text-sm font-medium">AI Overview Presence</span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">AI Overviews in SERP</p>
            <p className="text-xl font-bold">{kwWithAio}</p>
            <p className="text-xs text-muted-foreground">{aioPercent}% of keywords</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Your Site Cited</p>
            <p className="text-xl font-bold text-green-600 dark:text-green-400">{kwCited}</p>
            <p className="text-xs text-muted-foreground">{citedPercent}% of keywords</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Citation Rate</p>
            <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
              {kwWithAio > 0 ? Math.round((kwCited / kwWithAio) * 100) : 0}%
            </p>
            <p className="text-xs text-muted-foreground">of AIO SERPs</p>
          </div>
        </div>
        {/* Mini trend sparkline from aggregated data */}
        {data?.trend && data.trend.length > 1 && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-muted-foreground mb-1">AIO Trend (last 90 days)</p>
            <div className="flex items-end gap-0.5 h-6">
              {data.trend.map((w, i) => {
                const maxAio = Math.max(...data.trend.map((t) => t.with_aio), 1)
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    <div
                      className="w-full rounded-sm bg-purple-500/60"
                      style={{ height: `${(w.with_aio / maxAio) * 24}px`, minHeight: w.with_aio > 0 ? '2px' : '0' }}
                      title={`Week ${w.week_start}: ${w.with_aio} AIO, ${w.mentioned} cited`}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ── Main Component ───────────────────────────────────────────────

export function RankTrackerTab({ siteId, domain, defaultLocation, defaultEngine }: RankTrackerTabProps) {
  const { data, isLoading } = useKeywords(siteId)
  const enrichKeywords = useEnrichKeywords(siteId)
  const [sortKey, setSortKey] = useState<SortKey>('rank')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir(key === 'keyword' ? 'asc' : 'asc')
    }
  }

  const keywords = data?.keywords ?? []
  const sorted = useMemo(() => sortKeywords(keywords, sortKey, sortDir), [keywords, sortKey, sortDir])

  if (isLoading) return <Skeleton className="h-48 w-full" />

  if (!data?.configured) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Target className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <h3 className="text-lg font-semibold">DataForSEO Not Configured</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Rank tracking requires DataForSEO API credentials. Set the <code className="bg-muted px-1 py-0.5 rounded text-xs">DATAFORSEO_LOGIN</code> and <code className="bg-muted px-1 py-0.5 rounded text-xs">DATAFORSEO_PASSWORD</code> environment variables.
          </p>
          <p className="text-xs text-muted-foreground mt-3">
            DataForSEO rank tracking costs ~$0.0006/query. 100 keywords/day = ~$1.80/month.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              {keywords.length} keyword{keywords.length !== 1 ? 's' : ''} tracked
            </span>
          </div>
          <LastChecked keywords={keywords} />
        </div>
        <div className="flex items-center gap-2">
          {keywords.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => enrichKeywords.mutate()}
              disabled={enrichKeywords.isPending}
              title="Fetch search volume, CPC, and competition data from DataForSEO"
            >
              {enrichKeywords.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <BarChart3 className="mr-2 h-4 w-4" />
              )}
              Enrich
            </Button>
          )}
          {keywords.length > 0 && <CheckNowButton siteId={siteId} />}
          <AddKeywordsDialog siteId={siteId} domain={domain} existingKeywords={keywords} defaultLocation={defaultLocation} defaultEngine={defaultEngine} />
        </div>
      </div>

      {/* Position distribution */}
      <DistributionSummary keywords={keywords} />

      {/* AI Overview section */}
      {keywords.length > 0 && <AiOverviewSection siteId={siteId} keywords={keywords} />}

      {/* Keywords table */}
      {keywords.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
            <h3 className="text-lg font-semibold">No Keywords Tracked</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Add keywords to start monitoring your search engine rankings for {domain}.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <SortHeader label="Keyword" sortKey="keyword" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Rank" sortKey="rank" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="text-right" />
                    <SortHeader label="Change" sortKey="change" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="text-center" />
                    <th className="pb-2 font-medium text-center">Trend</th>
                    <SortHeader label="Volume" sortKey="volume" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="text-right" />
                    <SortHeader label="CPC" sortKey="cpc" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="text-right" />
                    <SortHeader label="Comp" sortKey="competition" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="text-right" />
                    <SortHeader label="KD" sortKey="difficulty" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="text-center" />
                    <th className="pb-2 font-medium text-center">Intent</th>
                    <th className="pb-2 font-medium text-center hidden xl:table-cell">SERP</th>
                    <th className="pb-2 font-medium text-right">URL</th>
                    <th className="pb-2 font-medium w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((kw) => (
                    <KeywordRow key={kw.id} keyword={kw} siteId={siteId} />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
